import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FileSignature, CheckCircle2, XCircle, Loader2 } from 'lucide-react'
import { usePreFlight, useFinalizeNegotiation } from '../../hooks/useNegotiations'
import { readDocumentBinding, writeDocumentBinding } from '../../hooks/useCustomXmlParts'
import type { NegotiationParty, SignerMapping } from '../../services/negotiationsApi'

interface SendToSigningButtonProps {
  negotiationId: number
  parties: NegotiationParty[]
  actorId?: string
  onSent?: () => void
}

export function SendToSigningButton({
  negotiationId,
  parties,
  actorId,
  onSent,
}: SendToSigningButtonProps) {
  const { t } = useTranslation()
  const [showPreFlight, setShowPreFlight] = useState(false)
  const [showSignerMapping, setShowSignerMapping] = useState(false)
  const [signerMappings, setSignerMappings] = useState<SignerMapping[]>([])
  const [includeCommentsPdf, setIncludeCommentsPdf] = useState(true)

  const preFlight = usePreFlight(negotiationId, showPreFlight)
  const finalizeMut = useFinalizeNegotiation()

  const activeParties = parties.filter((p) => p.is_active)
  const myParty = activeParties.find((p) => p.actor?.koblink_id === actorId)
  const canSend = myParty && (myParty.role === 'OWNER' || myParty.can_send_to_signing)

  if (!canSend) return null

  const handleStartPreFlight = () => {
    setShowPreFlight(true)
  }

  const handleProceedToSigners = () => {
    if (preFlight.data?.suggested_signers) {
      setSignerMappings(preFlight.data.suggested_signers)
    }
    setShowSignerMapping(true)
  }

  const handleFinalize = async () => {
    try {
      await finalizeMut.mutateAsync({
        negotiationId,
        signers: signerMappings,
        include_comments_pdf: includeCommentsPdf,
      })
      const existing = await readDocumentBinding().catch(() => null)
      if (existing) {
        await writeDocumentBinding({ ...existing, status: 'draft' }).catch(() => {})
      }
      onSent?.()
    } catch {
      // Error handled by mutation state
    }
  }

  const handleUpdateSignerEmail = (index: number, email: string) => {
    setSignerMappings((prev) =>
      prev.map((s, i) => (i === index ? { ...s, email } : s)),
    )
  }

  // Pre-flight results view
  if (showPreFlight && !showSignerMapping) {
    return (
      <div className="px-3 py-2 border-t border-neutral-100 dark:border-neutral-800 space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-[10px] font-medium text-neutral-600 dark:text-neutral-400">
            {t('negotiate.preFlightTitle', 'Pre-flight checks')}
          </span>
          <button
            onClick={() => setShowPreFlight(false)}
            className="text-[9px] text-neutral-400 hover:text-neutral-600"
          >
            {t('common.cancel', 'Cancel')}
          </button>
        </div>

        {preFlight.isLoading && (
          <div className="flex items-center gap-1.5 py-2 text-[10px] text-neutral-400">
            <Loader2 size={12} className="animate-spin" />
            {t('negotiate.preFlightRunning', 'Running checks...')}
          </div>
        )}

        {preFlight.data && (
          <>
            <div className="space-y-1">
              {preFlight.data.checks.map((check) => (
                <div key={check.key} className="flex items-start gap-1.5 text-[10px]">
                  {check.passed ? (
                    <CheckCircle2 size={12} className="text-success-500 shrink-0 mt-0.5" />
                  ) : (
                    <XCircle size={12} className="text-red-500 shrink-0 mt-0.5" />
                  )}
                  <div>
                    <span className={check.passed ? 'text-neutral-600 dark:text-neutral-400' : 'text-red-600 dark:text-red-400'}>
                      {check.label}
                    </span>
                    {check.details && (
                      <p className="text-[9px] text-neutral-400 mt-0.5">{check.details}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <button
              onClick={handleProceedToSigners}
              disabled={!preFlight.data.ready}
              className="w-full flex items-center justify-center gap-1 px-2 py-1.5 text-[10px] font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed rounded transition-all duration-200"
            >
              <FileSignature size={12} />
              {preFlight.data.ready
                ? t('negotiate.preFlightProceed', 'Map signers & finalize')
                : t('negotiate.preFlightBlocked', 'Resolve issues to proceed')}
            </button>
          </>
        )}

        {preFlight.isError && (
          <p className="text-[10px] text-red-500">
            {t('negotiate.preFlightError', 'Failed to run pre-flight checks. Try again.')}
          </p>
        )}
      </div>
    )
  }

  // Signer mapping view
  if (showSignerMapping) {
    return (
      <div className="px-3 py-2 border-t border-neutral-100 dark:border-neutral-800 space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-[10px] font-medium text-neutral-600 dark:text-neutral-400">
            {t('negotiate.signerMapping', 'Map signers')}
          </span>
          <button
            onClick={() => { setShowSignerMapping(false); setShowPreFlight(false) }}
            className="text-[9px] text-neutral-400 hover:text-neutral-600"
          >
            {t('common.cancel', 'Cancel')}
          </button>
        </div>

        <div className="space-y-1.5">
          {signerMappings.map((signer, index) => (
            <div key={signer.party_id} className="flex items-center gap-1.5">
              <span className="text-[10px] text-neutral-600 dark:text-neutral-400 w-20 truncate">
                {signer.party_label}
              </span>
              <input
                type="email"
                value={signer.email}
                onChange={(e) => handleUpdateSignerEmail(index, e.target.value)}
                placeholder="email@example.com"
                className="flex-1 px-2 py-1 text-[10px] border border-neutral-200 dark:border-neutral-600 rounded bg-white dark:bg-neutral-800 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </div>
          ))}
        </div>

        <label className="flex items-center gap-1.5 text-[10px] text-neutral-500">
          <input
            type="checkbox"
            checked={includeCommentsPdf}
            onChange={(e) => setIncludeCommentsPdf(e.target.checked)}
            className="rounded border-neutral-300"
          />
          {t('negotiate.includeCommentsPdf', 'Attach comments history PDF')}
        </label>

        {finalizeMut.isError && (
          <p className="text-[10px] text-red-500">
            {t('negotiate.finalizeError', 'Failed to finalize. Check signer details and try again.')}
          </p>
        )}

        <button
          onClick={handleFinalize}
          disabled={finalizeMut.isPending || signerMappings.some((s) => !s.email)}
          className="w-full flex items-center justify-center gap-1 px-2 py-1.5 text-[10px] font-medium text-white bg-success-600 hover:bg-success-700 disabled:opacity-50 disabled:cursor-not-allowed rounded transition-all duration-200"
        >
          {finalizeMut.isPending ? (
            <Loader2 size={12} className="animate-spin" />
          ) : (
            <FileSignature size={12} />
          )}
          {t('negotiate.finalizeAndSign', 'Finalize & send for signing')}
        </button>
      </div>
    )
  }

  // Default button
  return (
    <div className="px-3 py-2 border-t border-neutral-100 dark:border-neutral-800">
      <button
        onClick={handleStartPreFlight}
        disabled={finalizeMut.isPending}
        title={t('negotiate.sendToSigningConfirm')}
        className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-[10px] font-medium rounded-lg transition-colors disabled:opacity-50 text-white bg-success-600 hover:bg-success-700"
      >
        <FileSignature size={14} />
        {t('negotiate.sendToSigning')}
      </button>
    </div>
  )
}
